import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { pipelineService } from '../../../services/pipelineService';

const PipelineAnalytics = ({ projectId, stages }) => {
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      loadAnalytics();
    }
  }, [projectId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await pipelineService?.getPipelineAnalytics(projectId);
      setAnalytics(data || {});
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const getStageInfo = (stageName) => {
    return stages?.find(s => s?.stageName?.toLowerCase() === stageName?.toLowerCase()) || {};
  };

  const totalLeads = Object.values(analytics)?.reduce((sum, stage) => sum + (stage?.count || 0), 0);
  const totalValue = Object.values(analytics)?.reduce((sum, stage) => sum + (stage?.totalValue || 0), 0);

  if (loading) {
    return (
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Leads</p>
              <p className="text-3xl font-bold text-foreground">{totalLeads}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Users" size={24} className="text-primary" />
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Total Pipeline Value</p>
              <p className="text-3xl font-bold text-foreground">{formatCurrency(totalValue)}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
              <Icon name="DollarSign" size={24} className="text-green-600" />
            </div>
          </div>
        </div>
      </div>
      {/* Stage Breakdown */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center mb-6">
          <Icon name="BarChart3" size={24} className="mr-3 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">Stage Breakdown</h2>
        </div>

        <div className="space-y-4">
          {Object.entries(analytics)?.map(([stageName, data]) => {
            const stageInfo = getStageInfo(stageName);
            const percentage = totalLeads > 0 ? ((data?.count / totalLeads) * 100)?.toFixed(1) : 0;

            return (
              <div key={stageName} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
                      style={{ backgroundColor: `${stageInfo?.color || '#3B82F6'}20` }}
                    >
                      <Icon
                        name={stageInfo?.icon || 'Circle'}
                        size={16}
                        color={stageInfo?.color || '#3B82F6'}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{stageName}</p>
                      <p className="text-xs text-muted-foreground">
                        {data?.count} leads • {formatCurrency(data?.totalValue)}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-foreground">{percentage}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: stageInfo?.color || '#3B82F6'
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {Object.keys(analytics)?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="BarChart3" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No analytics data available</p>
            <p className="text-sm text-muted-foreground mt-2">Add leads to see pipeline analytics</p>
          </div>
        )}
      </div>
      {/* Conversion Insights */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center mb-4">
          <Icon name="TrendingUp" size={24} className="mr-3 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Conversion Insights</h2>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
            <span className="text-sm text-muted-foreground">Average Deal Size</span>
            <span className="text-sm font-semibold text-foreground">
              {formatCurrency(totalLeads > 0 ? totalValue / totalLeads : 0)}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
            <span className="text-sm text-muted-foreground">Total Stages</span>
            <span className="text-sm font-semibold text-foreground">{stages?.length}</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
            <span className="text-sm text-muted-foreground">Active Stages</span>
            <span className="text-sm font-semibold text-foreground">
              {stages?.filter(s => s?.isActive)?.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineAnalytics;